/**
 * User Setting Remapper
 * Flutter: lib/data/models/user_setting.dart
 */
import type { UserSettingDto } from '@/data/dtos/userSetting/userSettingDto';
import type { JsonObject } from '@/data/types/json';
import type { UserSettingStorage } from '@/domain/entities/secureStorage/secureStorageEntities';

export class UserSettingRemapper {
  static toStorage(dto: UserSettingDto): UserSettingStorage {
    return {
      name: dto.name,
      title: dto.title,
      description: dto.description,
      category: dto.category,
      dataType: dto.dataType,
      value: dto.value,
      defaultValue: dto.defaultValue,
      premiumPackage: dto.premiumPackage,
      literals: dto.literals,
    };
  }

  static fromJson(data: JsonObject): UserSettingDto {
    return {
      name: (data['name'] as string | undefined) ?? '',
      title: (data['title'] as string | undefined) ?? '',
      description: (data['description'] as string | undefined) ?? '',
      category: (data['category'] as number | undefined) ?? 0,
      dataType: (data['dataType'] as number | undefined) ?? 0,
      value: data['value'] as number | undefined,
      defaultValue: (data['defaultValue'] as number | undefined) ?? 0,
      premiumPackage: data['premiumPackage'] as string | undefined,
      literals: data['literals'] as string[] | undefined,
    };
  }

  static fromJsonList(dataList: JsonObject[]): UserSettingDto[] {
    return dataList.map(UserSettingRemapper.fromJson);
  }

  /**
   * Firestore write body for one `/users/{uid}/userSettings/{name}` document.
   *
   * Mirrors Flutter's `UserSetting.toSettingUpdateInfo()`
   * (lib/data/models/user_setting.dart:60-72) exactly — the same nine fields in the
   * same order. The Flutter model carries no `@JsonKey` renames, so every Firestore
   * field name equals the property name.
   *
   * `value` falls back to `defaultValue` when unset (`value ?? defaultValue`,
   * :67) and is therefore never null/undefined: a null `value` written into the
   * document would break the settings screens on both clients.
   *
   * `premiumPackage` and `literals` are always written, including when unset —
   * Flutter emits them as Dart `null` (:68-69). They are normalised to `null`
   * rather than left `undefined` because the Firestore JS SDK rejects `undefined`
   * field values (`ignoreUndefinedProperties` is not enabled on `db`), and `null`
   * is the exact wire value Flutter produces.
   */
  static toSettingUpdateInfo(dto: UserSettingDto): JsonObject {
    return {
      name: dto.name,
      title: dto.title,
      description: dto.description,
      category: dto.category,
      defaultValue: dto.defaultValue,
      value: dto.value ?? dto.defaultValue,
      premiumPackage: dto.premiumPackage ?? null,
      literals: dto.literals ?? null,
      dataType: dto.dataType,
    };
  }
}
